import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Star, Quote } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent } from '../components/ui/dialog';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Level Photography — Real Portfolio Assets
const HERO_IMG = "https://customer-assets.emergentagent.com/job_upload-tiers/artifacts/dj079our_8T8A9116.jpg";
const PORTRAIT_FAMILY = "https://customer-assets.emergentagent.com/job_upload-tiers/artifacts/h13bmgct_8T8A9862.jpg";
const PORTRAIT_STUDIO = "https://customer-assets.emergentagent.com/job_upload-tiers/artifacts/bf7hntnt_8T8A9929%202.jpg";
const GRAD_SENIOR = "https://customer-assets.emergentagent.com/job_upload-tiers/artifacts/kxgqmo3l_8T8A9140%202.jpg";
const GRAD_CAP = "https://customer-assets.emergentagent.com/job_upload-tiers/artifacts/aupr3pky_8T8A9135.jpg";
// Weddings & Events
const WEDDING_CAKE = "https://customer-assets.emergentagent.com/job_upload-tiers/artifacts/8lzpy1z9_8T8A2923.jpg";
const WEDDING_FAMILY = "https://customer-assets.emergentagent.com/job_upload-tiers/artifacts/xkf27m3o_8T8A2459.jpg";
const WEDDING_RECEPTION = "https://customer-assets.emergentagent.com/job_upload-tiers/artifacts/kqcx6sq3_8T8A2926.jpg";
const EVENT_ANNIV_TRIO = "https://customer-assets.emergentagent.com/job_upload-tiers/artifacts/apybx3mm_8T8A2511.jpg";
const EVENT_ANNIV_COUPLE = "https://customer-assets.emergentagent.com/job_upload-tiers/artifacts/sefxu1vj_8T8A2479.jpg";

const heroImage = HERO_IMG;

const services = [
  {
    title: "Wedding Photography",
    description: "Capture your special day with timeless elegance — from the ceremony to the cake-cutting, every moment preserved.",
    image: WEDDING_CAKE
  },
  {
    title: "Graduation & Portraits",
    description: "Bold, editorial graduation portraits and professional studio sessions built to highlight your story.",
    image: GRAD_CAP
  },
  {
    title: "Birthdays & Events",
    description: "Birthdays, anniversaries, and milestones — full coverage of every meaningful moment.",
    image: EVENT_ANNIV_TRIO
  }
];

const instagramHighlights = [
  { name: "Weddings", image: WEDDING_FAMILY },
  { name: "Birthdays", image: EVENT_ANNIV_TRIO },
  { name: "Graduation", image: GRAD_SENIOR },
  { name: "Studio", image: PORTRAIT_STUDIO },
  { name: "Family", image: PORTRAIT_FAMILY }
];

const galleryImages = [
  HERO_IMG,
  WEDDING_CAKE,
  EVENT_ANNIV_TRIO,
  PORTRAIT_STUDIO,
  WEDDING_FAMILY,
  EVENT_ANNIV_COUPLE
];

const testimonials = [
  {
    name: "Sarah & Michael",
    event: "Wedding",
    quote: "Level Photography captured our wedding day perfectly. Every moment was documented with such artistry and emotion.",
    rating: 5
  },
  {
    name: "Emily Johnson",
    event: "Portrait Session",
    quote: "The portrait session exceeded all my expectations. Professional, comfortable, and the results were stunning.",
    rating: 5
  },
  {
    name: "Corporate Events Inc.",
    event: "Annual Gala",
    quote: "We've worked with Level Photography for 3 years. Their consistency and quality are unmatched in the industry.",
    rating: 5
  }
];

const HomePage = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [photos, setPhotos] = useState([]);

  useEffect(() => {
    const fetchPhotos = async () => {
      try {
        const { data } = await axios.get(`${API}/api/photos/public`);
        if (data.length > 0) {
          setPhotos(data);
        }
      } catch (e) {
        console.error('Error fetching photos:', e);
      }
    };
    fetchPhotos();
  }, []);

  const displayGallery = photos.length > 0 
    ? photos.slice(0, 6).map(p => `${API}${p.image_url}`)
    : galleryImages;

  return (
    <div className="min-h-screen bg-black" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center" data-testid="hero-section">
        <div className="absolute inset-0">
          <img 
            src={heroImage} 
            alt="Level Photography Hero" 
            className="w-full h-full object-cover object-center"
            style={{ objectPosition: 'center 30%' }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/70"></div>
        </div>
        
        <div className="relative z-10 text-center px-4 max-w-4xl mx-auto">
          <h1 className="font-['Playfair_Display'] text-5xl sm:text-6xl md:text-7xl font-light tracking-tight text-white mb-6 animate-fade-in-up">
            Capturing Moments<br />at the Highest Level
          </h1>
          <p className="text-lg md:text-xl text-gray-300 font-light mb-10 animate-fade-in-up delay-200">
            Professional photography for every moment worth remembering
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in-up delay-300">
            <Link to="/booking" data-testid="hero-book-now-button">
              <Button className="btn-gold">
                Book a Session
              </Button>
            </Link>
            <Link to="/gallery" data-testid="hero-view-gallery-button">
              <Button className="btn-outline">
                View Gallery
              </Button>
            </Link>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <ChevronRight className="w-6 h-6 text-[#C9A14A] rotate-90" />
        </div>
      </section>

      {/* Instagram Highlights */}
      <section className="section-spacing bg-black" data-testid="highlights-section">
        <div className="container-luxury">
          <div className="text-center mb-12">
            <p className="text-[#C9A14A] uppercase tracking-[0.2em] text-sm mb-4">Our Work</p>
            <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl lg:text-5xl font-light text-white">
              Featured Categories
            </h2>
          </div>
          
          <div className="flex justify-center gap-6 md:gap-10 overflow-x-auto pb-4">
            {instagramHighlights.map((highlight, index) => (
              <div key={index} className="flex flex-col items-center flex-shrink-0" data-testid={`highlight-${highlight.name.toLowerCase()}`}>
                <div className="highlight-circle animate-pulse-gold">
                  <img src={highlight.image} alt={highlight.name} />
                </div>
                <span className="mt-3 text-sm text-gray-300 uppercase tracking-wider">
                  {highlight.name}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Gallery */}
      <section className="section-spacing bg-[#0a0a0a]" data-testid="gallery-section">
        <div className="container-luxury">
          <div className="text-center mb-12">
            <p className="text-[#C9A14A] uppercase tracking-[0.2em] text-sm mb-4">Portfolio</p>
            <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl lg:text-5xl font-light text-white">
              Featured Work
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {displayGallery.map((image, index) => (
              <div 
                key={index}
                className="img-zoom aspect-[4/5] cursor-pointer group"
                onClick={() => setSelectedImage(image)}
                data-testid={`gallery-image-${index}`}
              >
                <img 
                  src={image} 
                  alt={`Gallery ${index + 1}`}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-300 flex items-center justify-center opacity-0 group-hover:opacity-100">
                  <span className="text-white text-sm uppercase tracking-wider">View</span>
                </div>
              </div>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <Link to="/gallery" data-testid="view-all-gallery-button">
              <Button className="btn-outline">
                View Full Gallery
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="section-spacing bg-black" data-testid="services-section">
        <div className="container-luxury">
          <div className="text-center mb-12">
            <p className="text-[#C9A14A] uppercase tracking-[0.2em] text-sm mb-4">What We Offer</p>
            <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl lg:text-5xl font-light text-white">
              Our Services
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div 
                key={index}
                className="card-luxury group overflow-hidden"
                data-testid={`service-card-${index}`}
              >
                <div className="img-zoom h-64">
                  <img 
                    src={service.image} 
                    alt={service.title}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-6">
                  <h3 className="font-['Playfair_Display'] text-xl text-white mb-3">
                    {service.title}
                  </h3>
                  <p className="text-gray-400 text-sm mb-4">
                    {service.description}
                  </p>
                  <Link to="/booking" data-testid={`service-book-${index}`}>
                    <Button className="w-full btn-gold text-xs">
                      Book Now
                    </Button>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="section-spacing bg-[#0a0a0a]" data-testid="testimonials-section">
        <div className="container-luxury">
          <div className="text-center mb-12">
            <p className="text-[#C9A14A] uppercase tracking-[0.2em] text-sm mb-4">Testimonials</p>
            <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl lg:text-5xl font-light text-white">
              What Our Clients Say
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div 
                key={index}
                className="card-luxury p-8"
                data-testid={`testimonial-${index}`}
              >
                <Quote className="w-8 h-8 text-[#C9A14A] mb-4" />
                <p className="text-gray-300 text-sm leading-relaxed mb-6">
                  "{testimonial.quote}"
                </p>
                <div className="flex items-center gap-1 mb-3">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-[#C9A14A] text-[#C9A14A]" />
                  ))}
                </div>
                <p className="text-white font-medium">{testimonial.name}</p>
                <p className="text-gray-500 text-sm">{testimonial.event}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-spacing bg-black relative overflow-hidden" data-testid="cta-section">
        <div className="absolute inset-0 opacity-10">
          <img src={heroImage} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="container-luxury relative z-10 text-center">
          <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl lg:text-5xl font-light text-white mb-6">
            Ready to Book Your Session?
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto mb-8">
            Let us capture your most precious moments with artistry and precision. 
            Book your session today and experience photography at the highest level.
          </p>
          <Link to="/booking" data-testid="cta-book-now-button">
            <Button className="btn-gold">
              Book Now
            </Button>
          </Link>
        </div>
      </section>

      {/* Lightbox */}
      <Dialog open={!!selectedImage} onOpenChange={() => setSelectedImage(null)}>
        <DialogContent className="max-w-4xl bg-black/95 border-[#333] p-0" data-testid="lightbox-dialog">
          <img 
            src={selectedImage || ''} 
            alt="Gallery preview"
            className="w-full h-auto max-h-[80vh] object-contain"
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HomePage;
